import { SubmissionReportData } from "@shared/types";

import { PipelineArtifacts } from "@validator/index";
import { ValidSource, ClaudeManifest, InputFileType } from "@validator/schema";

import { ConsentTier, SubmissionFailure, Validation } from "@/types/submission";

// State types -----------------------------------------------------------------

type ValidateArtifact = PipelineArtifacts["validate"];

export type WriteSubmissionState =
  | { status: "idle" }
  | { status: "validating" }
  | { status: "validated" }
  | { status: "found_claude_manifest" }
  | { status: "uploading"; progress: number }
  | { status: "uploaded"; key: string }
  | { status: "file_error"; message: string }
  | { status: "validation_error"; message: string; errors?: string[] }
  | { status: "upload_error"; message: string };

export interface SubmissionState {
  writeState: WriteSubmissionState;
  consentTier: ConsentTier | null;
  validation: Validation | null;
  week: number | null;
  defaultWeek: number | null;
  overrideDefaultWeek: boolean;
}

export const initialSubmissionState: SubmissionState = {
  writeState: { status: "idle" },
  consentTier: null,
  validation: null,
  week: null,
  defaultWeek: null,
  overrideDefaultWeek: false,
};

// Actions ---------------------------------------------------------------------

export const WriteSubmissionAction = {
  Validating: "WRITE_SUBMISSION_VALIDATING",
  Validated: "WRITE_SUBMISSION_VALIDATED",
  FoundClaudeManifest: "WRITE_SUBMISSION_FOUND_CLAUDE_MANIFEST",
  Uploading: "WRITE_SUBMISSION_UPLOADING",
  Uploaded: "WRITE_SUBMISSION_UPLOADED",
  FileError: "WRITE_SUBMISSION_FILE_ERROR",
  ValidationError: "WRITE_SUBMISSION_VALIDATION_ERROR",
  UploadError: "WRITE_SUBMISSION_UPLOAD_ERROR",
} as const;

export const ConfigSubmissionAction = {
  SetConsentTier: "SET_SUBMISSION_CONSENT_TIER",
  Reset: "RESET_SUBMISSION",
  ResetValidation: "RESET_VALIDATION",
  SetUserSource: "SET_USER_SOURCE",
  SetWeek: "SET_WEEK",
  SetDefaultWeek: "SET_DEFAULT_WEEK",
  SetOverrideDefaultWeek: "SET_OVERRIDE_DEFAULT_WEEK",
  SetOverrideValidation: "SET_OVERRIDE_VALIDATION",
} as const;

type Action<T extends string, P = object> = { type: T } & P;

interface WSAValidatedProps {
  result: ValidateArtifact;
  file: File;
  truncated: boolean;
  report: SubmissionReportData | null;
}

interface WSAFoundClaudeManifestProps {
  manifest: ClaudeManifest;
  conversationsUrls: string[];
}

interface WSAValidationErrorProps {
  message: string;
  failure: SubmissionFailure;
  file: File;
  fileType: InputFileType;
  result: ValidateArtifact | null;
  truncated: boolean;
  report: SubmissionReportData | null;
}

export type SubmissionAction =
  // Write Submission
  | Action<typeof WriteSubmissionAction.Validating>
  | Action<typeof WriteSubmissionAction.Validated, WSAValidatedProps>
  | Action<typeof WriteSubmissionAction.FoundClaudeManifest, WSAFoundClaudeManifestProps>
  | Action<typeof WriteSubmissionAction.Uploading, { progress: number }>
  | Action<typeof WriteSubmissionAction.Uploaded, { key: string }>
  | Action<typeof WriteSubmissionAction.FileError, { message: string }>
  | Action<typeof WriteSubmissionAction.ValidationError, WSAValidationErrorProps>
  | Action<typeof WriteSubmissionAction.UploadError, { message: string }>
  // Config Submission
  | Action<typeof ConfigSubmissionAction.SetConsentTier, { tier: ConsentTier | null }>
  | Action<typeof ConfigSubmissionAction.Reset>
  | Action<typeof ConfigSubmissionAction.ResetValidation>
  | Action<typeof ConfigSubmissionAction.SetUserSource, { source: ValidSource | null }>
  | Action<typeof ConfigSubmissionAction.SetWeek, { value: number | null }>
  | Action<typeof ConfigSubmissionAction.SetDefaultWeek, { value: number | null }>
  | Action<typeof ConfigSubmissionAction.SetOverrideDefaultWeek, { value: boolean }>
  | Action<typeof ConfigSubmissionAction.SetOverrideValidation, { value: boolean }>;

export type Dispatch = React.Dispatch<SubmissionAction>;

// Handlers --------------------------------------------------------------------

type Handlers = {
  [A in SubmissionAction as A["type"]]: (state: SubmissionState, action: A) => SubmissionState;
};

const handlers: Handlers = {
  [WriteSubmissionAction.Validating]: (state) => ({
    ...state,
    writeState: { status: "validating" },
  }),
  [WriteSubmissionAction.Validated]: (state, action) => ({
    ...state,
    writeState: { status: "validated" },
    validation: {
      status: "accepted",
      result: action.result,
      file: action.file,
      truncated: action.truncated,
      report: action.report,
    },
  }),
  [WriteSubmissionAction.FoundClaudeManifest]: (state, action) => ({
    ...state,
    writeState: { status: "found_claude_manifest" },
    validation: {
      status: "claude_manifest",
      manifest: action.manifest,
      conversationsUrls: action.conversationsUrls,
    },
  }),
  [WriteSubmissionAction.Uploading]: (state, action) => ({
    ...state,
    writeState: { status: "uploading", progress: action.progress },
  }),
  [WriteSubmissionAction.Uploaded]: (state, action) => ({
    ...state,
    writeState: { status: "uploaded", key: action.key },
  }),
  [WriteSubmissionAction.FileError]: (state, action) => ({
    ...state,
    writeState: { status: "file_error", message: action.message },
  }),
  [WriteSubmissionAction.ValidationError]: (state, action) => ({
    ...state,
    writeState: {
      status: "validation_error",
      message: action.message,
      errors: action.failure.errors,
    },
    validation: {
      status: "rejected",
      failure: action.failure,
      file: action.file,
      fileType: action.fileType,
      overrideValidation: false,
      userSource: null,
      result: action.result,
      truncated: action.truncated,
      report: action.report,
    },
  }),
  [WriteSubmissionAction.UploadError]: (state, action) => ({
    ...state,
    writeState: { status: "upload_error", message: action.message },
  }),

  [ConfigSubmissionAction.SetConsentTier]: (state, action) => ({
    ...state,
    consentTier: action.tier,
  }),
  [ConfigSubmissionAction.Reset]: (state) => ({
    ...initialSubmissionState,
    defaultWeek: state.defaultWeek,
  }),
  [ConfigSubmissionAction.ResetValidation]: (state) => ({
    ...state,
    writeState: { status: "idle" },
    validation: null,
  }),
  [ConfigSubmissionAction.SetUserSource]: (state, action) =>
    state.validation?.status === "rejected"
      ? { ...state, validation: { ...state.validation, userSource: action.source } }
      : state,
  [ConfigSubmissionAction.SetWeek]: (state, action) => ({
    ...state,
    week: action.value,
  }),
  [ConfigSubmissionAction.SetDefaultWeek]: (state, action) => ({
    ...state,
    defaultWeek: action.value,
  }),
  [ConfigSubmissionAction.SetOverrideDefaultWeek]: (state, action) => ({
    ...state,
    overrideDefaultWeek: action.value,
  }),
  [ConfigSubmissionAction.SetOverrideValidation]: (state, action) =>
    state.validation?.status === "rejected"
      ? { ...state, validation: { ...state.validation, overrideValidation: action.value } }
      : state,
};

// Reducer ---------------------------------------------------------------------

export function submissionReducer(
  state: SubmissionState,
  action: SubmissionAction,
): SubmissionState {
  const handler = handlers[action.type] as
    | ((s: SubmissionState, a: SubmissionAction) => SubmissionState)
    | undefined;
  return handler ? handler(state, action) : state;
}
